import * as React from 'react';

import {
  CampaignApiError,
  type CampaignListItemDto,
} from '@/api/campaign-client';
import type { DashboardCampaignRowDto } from '@/api/operational-view-client';
import {
  useAddClientScope,
  useCampaignsList,
  useCloneCampaign,
  useCreateCampaign,
} from '@/api/use-campaigns';
import { useCampaignsDashboard } from '@/api/use-operational-view';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MenuButton } from '@/components/ui/menu-button';
import { Modal } from '@/components/ui/modal';
import { Panel } from '@/components/ui/panel';
import {
  campaignStateInBucket,
  campaignStatusBucketLabel,
  CAMPAIGN_STATUS_BUCKETS,
  isCampaignStatusBucket,
  type CampaignStatusBucket,
} from '@/lib/campaign-labels';
import {
  CriticalityBadge,
  CycleDeadline,
  formatShortDate,
  plural,
  REGISTRY_STATE,
  SortableHeader,
  StatusLabel,
  type SortState,
} from '@/routes/admin/campaign-registry-format';
import { CampaignSuppliersFocus } from '@/routes/admin/campaigns/campaign-suppliers-focus';

// =============================================================================
// campaigns-list-screen.tsx — Écran A « Campagnes » (Admin) : liste + création.
// =============================================================================
// GET /admin/campaigns (liste) + POST /admin/campaigns (création) + POST
// /admin/campaigns/:id/client-scopes (périmètre clients à la création, DBU §2.5
// l.1101) + POST /admin/campaigns/:id/clone (duplication, DBU §2.5 l.1119).
//
// CRÉATION EN MODALE (DBU §2.5 l.1097, item 12) : le bouton « Créer une campagne »
// OUVRE une modale (plus de carte inline permanente). La modale porte l'INTITULÉ +
// la SÉLECTION DES CLIENTS (l.1101, item 13) : back V-CREATE réutilisé — la campagne
// est créée (intitulé seul, figement catalog_version_id serveur), PUIS chaque SIREN
// client valide est posé via client-scopes (le sélecteur reste AUSSI dans le détail
// pour édition ultérieure du périmètre).
//
// DUPLICATION PAR LIGNE (DBU §2.5 l.1119, item 19) : chaque ligne du registre porte
// un MENU CONTEXTUEL ; la commande « Dupliquer » n'y est ACTIVE que pour les campagnes
// TERMINALES — Publiee (« Clôturée » côté DBU) / Abandonnee (« Archivée »). Clone back
// réutilisé ; la copie repart en Brouillon avec bandeau de provenance.
//
// FIGEMENT catalog_version_id (GATE_0 S10) : la création estampille la version
// PUBLISHED courante. Si AUCUNE version PUBLISHED n'existe, la création est REFUSÉE
// (409) → message métier explicite dans la modale (CampaignApiError.kind ===
// 'no-published-catalog'), pas un crash ; tout autre message remonte tel quel.
//
// WCAG 2.1 AA : modale accessible (focus-trap, Échap), <table> avec <caption>,
// <th scope>, états en LIBELLÉ texte (badge = renfort couleur), formulaires <label>
// liés, messages role="status"/role="alert", menu contextuel clavier-navigable.
//
// A-12 (DBU §2.5 l.1068/1094) : filtre par STATUT OPÉRATIONNEL (4 buckets métier ;
// « Tous » par défaut). Le filtre vit dans le SEARCH PARAM d'URL (RULE_REF_006, jamais
// localStorage) ; l'écran reçoit la valeur + un callback et FILTRE côté front.
//
// A-12 — REFONTE (maquette John 2026-09-16, parité tableau de bord A-02) : conteneur 1400 px, registre en
// gabarit Panel (« Registre des opérations » + compteur + filtre « Statut »), 6 colonnes — Intitulé (+ badge
// Critique/Attention) · Statut (pastille + texte) · Créée le · Échéances (C1 15 sept. J−1 / +N j) · Volumétrie
// · actions (« Ouvrir » + menu « Dupliquer »). Colonne « Référence catalogue » SUPPRIMÉE (GATE_HUMAN, point 2).
// Les rangs proviennent de `useCampaignsList` (DTO requis pour créer / dupliquer / sélectionner), ENRICHIS par
// le read-model MACROSCOPIQUE déjà exposé (`useCampaignsDashboard`) joint PAR campaignId ; enrichissement
// RÉSILIENT (colonnes en « — » si indisponible). Habillage partagé : `campaign-registry-format.tsx`.
//
// SÉLECTION DE LIGNE (clic ligne ou bouton clavier aria-expanded) : ouvre la SECTION INFÉRIEURE
// « Fournisseurs — {intitulé} » (`campaign-suppliers-focus.tsx` : pilotage, indicateurs, alertes, suivi
// fournisseurs et relances). Une seule à la fois ; ligne sélectionnée surlignée (bande + rail gauche).
//
// WCAG 2.1 AA : criticité / statut / retard doublés d'un LIBELLÉ TEXTE ; tri boutonné + aria-sort ; dates
// dans le FUSEAU DE L'UTILISATEUR (pas de `timeZone:'UTC'` forcé).

/** Sémantique DBU : la duplication n'est offerte que pour les campagnes TERMINALES. */
function isCampaignDuplicable(row: CampaignListItemDto): boolean {
  return row.state === 'Publiee' || row.state === 'Abandonnee';
}

/** Date courante AAAA-MM-JJ (UTC) — suffixe d'intitulé de duplication (DBU §2.5 l.1149). */
function todayDate(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Découpe une saisie multi-SIREN (séparés par espace, virgule, point-virgule, saut
 * de ligne) en SIREN valides (9 chiffres, dédupliqués) et rejetés (invalides/doublons).
 */
function parseSirens(raw: string): {
  readonly valid: readonly string[];
  readonly rejected: readonly string[];
} {
  const tokens = raw.split(/[\s,;]+/).filter((t) => t.length > 0);
  const seen = new Set<string>();
  const valid: string[] = [];
  const rejected: string[] = [];
  for (const token of tokens) {
    if (!/^\d{9}$/.test(token) || seen.has(token)) {
      rejected.push(token);
      continue;
    }
    seen.add(token);
    valid.push(token);
  }
  return { valid, rejected };
}

export interface CampaignsListScreenProps {
  /** Ouvre le détail d'une campagne (navigation portée par l'adaptateur routeur). */
  readonly onOpen: (campaignId: string) => void;
  /** Filtre de statut opérationnel courant (search param d'URL ; défaut « tous »). */
  readonly statusFilter: CampaignStatusBucket;
  /** Change le filtre de statut (l'adaptateur routeur met à jour l'URL). */
  readonly onChangeStatusFilter: (bucket: CampaignStatusBucket) => void;
}

export function CampaignsListScreen({
  onOpen,
  statusFilter,
  onChangeStatusFilter,
}: CampaignsListScreenProps): React.JSX.Element {
  const list = useCampaignsList();
  // Read-model MACROSCOPIQUE analytics DÉJÀ exposé : criticité + échéances + volumétrie, jointes PAR
  // campaignId. Enrichissement RÉSILIENT (la liste reste rendue si indisponible).
  const dashboard = useCampaignsDashboard();
  const enrichmentByCampaignId = React.useMemo(() => {
    const map = new Map<string, DashboardCampaignRowDto>();
    for (const row of dashboard.data?.campaigns ?? []) {
      map.set(row.campaignId, row);
    }
    return map;
  }, [dashboard.data]);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [dupTarget, setDupTarget] =
    React.useState<CampaignListItemDto | null>(null);

  return (
    <div className="mx-auto w-full max-w-[1400px] space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-[30px] font-medium tracking-tight">Campagnes</h1>
          <p className="text-muted-foreground">
            Création, duplication et suivi des fournisseurs
            {list.data !== undefined
              ? ` · ${plural(list.data.length, 'campagne')}`
              : null}
          </p>
        </div>
        <Button type="button" onClick={() => setCreateOpen(true)}>
          Créer une campagne
        </Button>
      </header>

      <CreateCampaignDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(campaignId) => {
          setCreateOpen(false);
          onOpen(campaignId);
        }}
      />

      {/* Une seule instance de modale de duplication, re-montée par clé de source
          pour ré-initialiser l'intitulé pré-rempli à chaque cible choisie. */}
      <DuplicateCampaignDialog
        key={dupTarget?.campaignId ?? 'none'}
        source={dupTarget}
        onClose={() => setDupTarget(null)}
        onDuplicated={(campaignId) => {
          setDupTarget(null);
          onOpen(campaignId);
        }}
      />

      <CampaignsRegistry
        list={list}
        onOpen={onOpen}
        onDuplicate={setDupTarget}
        statusFilter={statusFilter}
        onChangeStatusFilter={onChangeStatusFilter}
        enrichmentByCampaignId={enrichmentByCampaignId}
        enrichmentPending={dashboard.isPending}
      />
    </div>
  );
}

// -----------------------------------------------------------------------------
// Modale de création (DBU §2.5 l.1097/1101 — items 12 + 13)
// -----------------------------------------------------------------------------

interface CreateCampaignDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  /** Campagne créée (et clients éventuels posés) : navigue vers le détail. */
  readonly onCreated: (campaignId: string) => void;
}

function CreateCampaignDialog({
  open,
  onClose,
  onCreated,
}: CreateCampaignDialogProps): React.JSX.Element {
  const create = useCreateCampaign();
  const addClient = useAddClientScope();
  const [intitule, setIntitule] = React.useState('');
  const [sirenInput, setSirenInput] = React.useState('');
  const [rejected, setRejected] = React.useState<readonly string[]>([]);
  const [submitting, setSubmitting] = React.useState(false);
  // SIREN posés mais refusés par l'API (ex. client inconnu) — non bloquant : la
  // campagne existe, le périmètre reste éditable dans le détail.
  const [scopeWarning, setScopeWarning] = React.useState<readonly string[]>([]);

  const onSubmit = React.useCallback(
    (event: React.FormEvent<HTMLFormElement>): void => {
      event.preventDefault();
      const { valid, rejected: bad } = parseSirens(sirenInput);
      setRejected(bad);
      setScopeWarning([]);
      setSubmitting(true);
      void (async () => {
        try {
          const campaign = await create.mutateAsync({ intitule });
          const failed: string[] = [];
          for (const clientSiren of valid) {
            try {
              await addClient.mutateAsync({
                campaignId: campaign.campaignId,
                body: { clientSiren },
              });
            } catch {
              failed.push(clientSiren);
            }
          }
          if (failed.length > 0) {
            // La campagne est créée : on ouvre le détail (périmètre éditable là-bas).
            setScopeWarning(failed);
          }
          onCreated(campaign.campaignId);
        } catch {
          // Échec de création (ex. figement 409) : message via create.error, modale reste ouverte.
        } finally {
          setSubmitting(false);
        }
      })();
    },
    [create, addClient, intitule, sirenInput, onCreated],
  );

  // FIGEMENT : message métier explicite si aucune version PUBLISHED (409).
  const isFigement =
    create.error instanceof CampaignApiError &&
    create.error.kind === 'no-published-catalog';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Créer une campagne"
      description="Saisissez un intitulé et, si besoin, un ou plusieurs clients. La version de catalogue publiée courante est figée automatiquement à la création."
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="campaign-intitule">Intitulé de la campagne</Label>
          <Input
            id="campaign-intitule"
            name="intitule"
            value={intitule}
            onChange={(e) => setIntitule(e.target.value)}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="campaign-create-clients">
            Client(s) concerné(s) — optionnel
          </Label>
          <textarea
            id="campaign-create-clients"
            name="clientSiren"
            inputMode="numeric"
            value={sirenInput}
            onChange={(e) => setSirenInput(e.target.value)}
            rows={2}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          />
          <p className="text-xs text-muted-foreground">
            Sélection unique ou multiple : un ou plusieurs SIREN (9 chiffres)
            séparés par une virgule, un espace ou un saut de ligne. Le périmètre
            reste éditable dans le détail.
          </p>
        </div>

        {rejected.length > 0 ? (
          <p role="alert" className="text-sm text-destructive">
            SIREN ignorés (format invalide ou doublon) : {rejected.join(', ')}.
          </p>
        ) : null}

        {scopeWarning.length > 0 ? (
          <p role="alert" className="text-sm text-destructive">
            Clients non rattachés (à reprendre dans le détail) :{' '}
            {scopeWarning.join(', ')}.
          </p>
        ) : null}

        {/* FIGEMENT (409) : erreur métier explicite, lisible (pas un crash). */}
        {isFigement ? (
          <p
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
          >
            Aucune version de catalogue publiée — publiez une version avant de
            créer une campagne.
          </p>
        ) : create.isError ? (
          <p role="alert" className="text-sm text-destructive">
            {create.error.message}
          </p>
        ) : null}

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Création…' : 'Créer la campagne'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// -----------------------------------------------------------------------------
// Modale de duplication (DBU §2.5 l.1119/1149 — item 19)
// -----------------------------------------------------------------------------

interface DuplicateCampaignDialogProps {
  /** Campagne source à dupliquer ; `null` = modale fermée. */
  readonly source: CampaignListItemDto | null;
  readonly onClose: () => void;
  /** Copie créée (Brouillon) : navigue vers son détail. */
  readonly onDuplicated: (campaignId: string) => void;
}

function DuplicateCampaignDialog({
  source,
  onClose,
  onDuplicated,
}: DuplicateCampaignDialogProps): React.JSX.Element | null {
  const clone = useCloneCampaign();
  // Intitulé PRÉ-REMPLI suffixé par la date courante (DBU §2.5 l.1149) ; ajustable.
  const [intitule, setIntitule] = React.useState(() =>
    source === null ? '' : `${source.intitule} — ${todayDate()}`,
  );

  const onSubmit = React.useCallback(
    (event: React.FormEvent<HTMLFormElement>): void => {
      event.preventDefault();
      if (source === null) {
        return;
      }
      clone.mutate(
        { campaignId: source.campaignId, body: { intitule } },
        { onSuccess: (created) => onDuplicated(created.campaignId) },
      );
    },
    [clone, source, intitule, onDuplicated],
  );

  if (source === null) {
    return null;
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Dupliquer la campagne"
      description="Crée une nouvelle campagne en Brouillon à partir de ce gabarit (clients, fournisseurs et profils repris ; dates réinitialisées ; version de catalogue ré-figée ; données de saisie et statistiques exclues). Un bandeau de provenance est ajouté à la copie."
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="duplicate-intitule">Intitulé de la copie</Label>
          <Input
            id="duplicate-intitule"
            name="intitule"
            value={intitule}
            onChange={(e) => setIntitule(e.target.value)}
            required
          />
        </div>

        {clone.isError ? (
          <p role="alert" className="text-sm text-destructive">
            {clone.error.message}
          </p>
        ) : null}

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button type="submit" disabled={clone.isPending}>
            {clone.isPending ? 'Duplication…' : 'Dupliquer la campagne'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// -----------------------------------------------------------------------------
// Registre (Panel + tableau 6 colonnes) + menu contextuel par ligne + section inférieure
// -----------------------------------------------------------------------------

type SortKey = 'intitule' | 'state' | 'createdAt';

/** Identifiant DOM de la section inférieure (cible aria-controls des boutons de sélection). */
const FOCUS_SECTION_ID = 'campaign-suppliers-focus';

/** Comparateur d'une colonne (ordre croissant ; le sens est appliqué par l'appelant). */
function compareByKey(
  a: CampaignListItemDto,
  b: CampaignListItemDto,
  key: SortKey,
): number {
  switch (key) {
    case 'intitule':
      return a.intitule.localeCompare(b.intitule, 'fr');
    case 'state':
      return REGISTRY_STATE[a.state].label.localeCompare(
        REGISTRY_STATE[b.state].label,
        'fr',
      );
    case 'createdAt':
      return a.createdAt.localeCompare(b.createdAt);
  }
}

interface CampaignsRegistryProps {
  readonly list: ReturnType<typeof useCampaignsList>;
  readonly onOpen: (campaignId: string) => void;
  readonly onDuplicate: (row: CampaignListItemDto) => void;
  readonly statusFilter: CampaignStatusBucket;
  readonly onChangeStatusFilter: (bucket: CampaignStatusBucket) => void;
  /** Enrichissement analytics par campaignId (criticité / échéances / volumétrie). */
  readonly enrichmentByCampaignId: ReadonlyMap<string, DashboardCampaignRowDto>;
  /** L'enrichissement analytics est en cours de chargement (colonnes en « … » vs « — »). */
  readonly enrichmentPending: boolean;
}

function CampaignsRegistry({
  list,
  onOpen,
  onDuplicate,
  statusFilter,
  onChangeStatusFilter,
  enrichmentByCampaignId,
  enrichmentPending,
}: CampaignsRegistryProps): React.JSX.Element {
  // Tri client sur les données déjà chargées. Défaut : plus récentes d'abord (parité dashboard).
  const [sort, setSort] = React.useState<SortState<SortKey>>({
    key: 'createdAt',
    dir: 'desc',
  });
  // Campagne SÉLECTIONNÉE → section « Fournisseurs » SOUS le registre (une à la fois).
  const [selectedId, setSelectedId] = React.useState<string | null>(null);

  // Filtrage FRONT sur les données déjà chargées (contrat listCampaigns figé, sans query param).
  const rows = React.useMemo(() => {
    if (list.data === undefined) {
      return [];
    }
    const filtered = list.data.filter((row) =>
      campaignStateInBucket(row.state, statusFilter),
    );
    const sorted = [...filtered].sort((a, b) => compareByKey(a, b, sort.key));
    return sort.dir === 'asc' ? sorted : sorted.reverse();
  }, [list.data, statusFilter, sort]);

  const onSort = React.useCallback((key: SortKey): void => {
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: key === 'createdAt' ? 'desc' : 'asc' },
    );
  }, []);

  const onToggle = React.useCallback((campaignId: string): void => {
    setSelectedId((prev) => (prev === campaignId ? null : campaignId));
  }, []);

  const ready = !list.isPending && !list.isError;
  const selectedRow = rows.find((r) => r.campaignId === selectedId) ?? null;

  return (
    <>
      <Panel
        headingId="campaigns-list-heading"
        title="Registre des opérations"
        count={ready ? rows.length : undefined}
        aside={
          <>
            <label htmlFor="campaigns-status-filter">Statut</label>
            <select
              id="campaigns-status-filter"
              name="statusFilter"
              value={statusFilter}
              onChange={(e) => {
                const next = e.target.value;
                if (isCampaignStatusBucket(next)) {
                  onChangeStatusFilter(next);
                }
              }}
              className="h-9 w-48 rounded-md border border-input bg-background px-3 text-sm text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              {CAMPAIGN_STATUS_BUCKETS.map((bucket) => (
                <option key={bucket} value={bucket}>
                  {campaignStatusBucketLabel(bucket)}
                </option>
              ))}
            </select>
          </>
        }
      >
        {list.isPending ? (
          <p role="status" className="px-6 py-4 text-sm text-muted-foreground">
            Chargement des campagnes…
          </p>
        ) : list.isError ? (
          <p role="alert" className="px-6 py-4 text-sm text-destructive">
            Erreur de chargement : {list.error.message}
          </p>
        ) : list.data.length === 0 ? (
          <p
            role="status"
            className="px-6 py-10 text-center text-sm text-muted-foreground"
          >
            Aucune campagne pour le moment.
          </p>
        ) : rows.length === 0 ? (
          <p
            role="status"
            className="px-6 py-10 text-center text-sm text-muted-foreground"
          >
            Aucune campagne pour le statut «{' '}
            {campaignStatusBucketLabel(statusFilter)} ».
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="ds-table w-full border-collapse text-sm">
              <caption className="sr-only">
                Registre des campagnes (intitulé et criticité, statut, date de création,
                échéances, volumétrie) — colonnes triables ; sélectionner une ligne affiche
                le suivi des fournisseurs sous le registre
              </caption>
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left">
                  <SortableHeader
                    label="Intitulé"
                    sortKey="intitule"
                    sort={sort}
                    onSort={onSort}
                    className="pl-6"
                  />
                  <SortableHeader
                    label="Statut"
                    sortKey="state"
                    sort={sort}
                    onSort={onSort}
                  />
                  <SortableHeader
                    label="Créée le"
                    sortKey="createdAt"
                    sort={sort}
                    onSort={onSort}
                  />
                  <th scope="col" className="whitespace-nowrap px-4 py-3">
                    Échéances
                    <span className="normal-case tracking-normal text-dash-late">
                      {' '}
                      · retard en rouge
                    </span>
                  </th>
                  <th scope="col" className="whitespace-nowrap px-4 py-3">
                    Volumétrie
                  </th>
                  <th scope="col" className="py-3 pl-4 pr-6">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <RegistryRow
                    key={row.campaignId}
                    row={row}
                    enriched={enrichmentByCampaignId.get(row.campaignId)}
                    enrichmentPending={enrichmentPending}
                    isSelected={row.campaignId === selectedId}
                    onOpen={onOpen}
                    onDuplicate={onDuplicate}
                    onToggle={onToggle}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {selectedRow !== null ? (
        <CampaignSuppliersFocus
          key={selectedRow.campaignId}
          sectionId={FOCUS_SECTION_ID}
          campaign={selectedRow}
          enriched={enrichmentByCampaignId.get(selectedRow.campaignId)}
          onClose={() => setSelectedId(null)}
        />
      ) : null}
    </>
  );
}

/** Repli d'une cellule enrichie : « … » pendant le chargement, « — » si indisponible. */
function EnrichmentFallback({
  pending,
}: {
  readonly pending: boolean;
}): React.JSX.Element {
  return <span className="text-muted-foreground">{pending ? '…' : '—'}</span>;
}

function RegistryRow({
  row,
  enriched,
  enrichmentPending,
  isSelected,
  onOpen,
  onDuplicate,
  onToggle,
}: {
  readonly row: CampaignListItemDto;
  readonly enriched: DashboardCampaignRowDto | undefined;
  readonly enrichmentPending: boolean;
  readonly isSelected: boolean;
  readonly onOpen: (campaignId: string) => void;
  readonly onDuplicate: (row: CampaignListItemDto) => void;
  readonly onToggle: (campaignId: string) => void;
}): React.JSX.Element {
  const duplicable = isCampaignDuplicable(row);
  const isCancelled = row.state === 'Abandonnee';
  // Compte à rebours / retard pertinent tant que la campagne est en cours (parité dashboard).
  const showCountdown = campaignStateInBucket(row.state, 'en-cours');
  const rail = isSelected
    ? 'shadow-[inset_3px_0_0_0_hsl(var(--foreground))]'
    : '';

  return (
    <tr
      aria-selected={isSelected}
      // Clic souris n'importe où sur la ligne = sélectionner / désélectionner. L'équivalent clavier est le
      // bouton de la 1re cellule (aria-expanded) ; les clics issus d'un bouton (Ouvrir, menu) sont ignorés.
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button, a') !== null) {
          return;
        }
        onToggle(row.campaignId);
      }}
      className={`cursor-pointer border-b border-border last:border-b-0 ${
        isSelected ? 'bg-dash-band' : ''
      } ${isCancelled ? 'text-dash-muted-row' : ''}`}
    >
      <td className={`min-w-[12rem] py-3.5 pl-6 pr-4 ${rail}`}>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <button
            type="button"
            aria-expanded={isSelected}
            aria-controls={isSelected ? FOCUS_SECTION_ID : undefined}
            aria-label={`Afficher les fournisseurs de la campagne ${row.intitule}`}
            onClick={() => onToggle(row.campaignId)}
            // P-2 (2026-09-23) : ce bouton ouvre le panneau « Suivi fournisseurs », d'où part la
            // RELANCE des non-soumis. Il était `sr-only` hors sélection : invisible à l'œil, atteignable
            // au seul clavier. Le clic sur la ligne entière fonctionnait aussi, mais RIEN ne l'annonçait
            // — d'où le constat de production « la relance n'est plus accessible ». L'affordance est
            // désormais VISIBLE en permanence ; la rotation dit l'état (ouvert/fermé), le libellé
            // accessible porte déjà le sens pour les lecteurs d'écran.
            className={
              isSelected
                ? 'text-xs text-muted-foreground'
                : 'text-xs text-muted-foreground/70 transition-colors hover:text-foreground'
            }
          >
            <span
              aria-hidden="true"
              className={isSelected ? 'inline-block' : 'inline-block -rotate-90'}
            >
              ▼
            </span>
          </button>
          <span className="font-medium text-foreground">{row.intitule}</span>
          {enriched !== undefined ? (
            <CriticalityBadge criticality={enriched.criticality} />
          ) : null}
        </div>
      </td>
      <td className="px-4 py-3.5">
        <StatusLabel state={row.state} />
      </td>
      <td className="whitespace-nowrap px-4 py-3.5 tabular-nums">
        {formatShortDate(row.createdAt)}
      </td>
      <td className="px-4 py-3.5 tabular-nums">
        {enriched === undefined ? (
          <EnrichmentFallback pending={enrichmentPending} />
        ) : enriched.cycles.length === 0 ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          enriched.cycles.map((cycle, i) => (
            <React.Fragment key={cycle.cycleNumber}>
              {i > 0 ? ' · ' : null}
              <CycleDeadline cycle={cycle} showCountdown={showCountdown} />
            </React.Fragment>
          ))
        )}
      </td>
      <td className="px-4 py-3.5 tabular-nums">
        {enriched === undefined ? (
          <EnrichmentFallback pending={enrichmentPending} />
        ) : (
          <>
            {/* Retour à la ligne permis entre les deux mesures (jamais au milieu) : le tableau tient à 1280 px. */}
            <span className="whitespace-nowrap">
              {plural(enriched.invitedSupplierCount, 'fournisseur')}
            </span>{' '}
            ·{' '}
            <span className="whitespace-nowrap">
              {plural(enriched.profileCount, 'profil')}
            </span>
          </>
        )}
      </td>
      <td className="py-3.5 pl-4 pr-6 text-right">
        <div className="flex items-center justify-end gap-2 text-foreground">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onOpen(row.campaignId)}
            aria-label={`Ouvrir la campagne ${row.intitule}`}
            className={
              isSelected
                ? 'min-w-20 border-foreground bg-foreground text-background hover:bg-foreground/90 hover:text-background'
                : 'min-w-20 text-foreground'
            }
          >
            Ouvrir
          </Button>
          <MenuButton
            label={`Actions pour la campagne ${row.intitule}`}
            items={[
              {
                // « Dupliquer » n'est active que sur Clôturée (Publiée) /
                // Archivée (Abandonnée) — DBU §2.5 l.1119/1145.
                label: duplicable
                  ? 'Dupliquer la campagne'
                  : 'Dupliquer — Clôturées / Archivées uniquement',
                onSelect: () => onDuplicate(row),
                disabled: !duplicable,
              },
            ]}
          />
        </div>
      </td>
    </tr>
  );
}
